<!-- src/components/boardMain.vue -->
<template>
  <div id="dialog-board-main">

    <dialog_boardView v-if="watchingPost" :thePost="watchingPost" :windowOnOff="boardViewOnOff" :reloadPost="fetchBoards" />
    <dialog_boardEdit v-if="postEditorVisible" :thePost="watchingPost" :windowOnOff="boardEditOnOff" :reloadPost="fetchBoards" />

    <div class="reference">

      <!-- Header with subtle animation -->
      <header class="header">
        <button class="back-btn" @click="oth_showBoardMain(false)" aria-label="뒤로가기">
          <i class="ph-bold ph-arrow-left"></i>
        </button>
        <div class="header-title">
          <i class="ph-duotone ph-folder-open title-icon"></i>
          <h1>자료실</h1>
        </div>
        <button class="menu-btn" aria-label="메뉴">
          <i class="ph-bold ph-dots-three"></i>
        </button>
      </header>

      <div class="content">

        <!-- Sidebar Menu -->
        <nav class="sidebar">
          <div class="sidebar-header">
            <span class="sidebar-label">카테고리</span>
          </div>
          <ul class="menu">
            <li 
              :class="['menu-item', { active: selBoard === 'all' }]" 
              @click="selBoard = 'all'"
            >
              <div class="menu-item-content">
                <i class="ph-duotone ph-squares-four"></i>
                <span>전체</span>
              </div>
              <span class="menu-count">{{ totalCount }}</span>
            </li>
            <li 
              v-for="board in boards" 
              :key="board.idx" 
              :class="['menu-item', { active: selBoard === board.idx }]"
              @click="selBoard = board.idx"
            >
              <div class="menu-item-content">
                <i class="ph-duotone ph-folder"></i>
                <span>{{ board.name }}</span>
              </div>
              <span class="menu-count">{{ itemsByBoard[board.idx]?.length || 0 }}</span>
            </li>
          </ul>
        </nav>

        <!-- Main Content Area -->
        <main class="list-area">
          
          <!-- Search Header -->
          <div class="content-header">
            <div class="header-info">
              <h2 class="current-category">
                {{ selBoard === 'all' ? '전체 자료' : boards.find(b => b.idx === selBoard)?.name || '' }}
              </h2>
              <span class="item-count">
                {{ selBoard === 'all' ? totalCount : (itemsByBoard[selBoard]?.length || 0) }}개의 자료
              </span>
            </div>
            <div class="search-box" :class="{ focused: searchFocused }">
              <i class="ph ph-magnifying-glass search-icon"></i>
              <input 
                type="text" 
                v-model="searchQuery"
                placeholder="자료 검색..." 
                @focus="searchFocused = true"
                @blur="searchFocused = false"
              />
              <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''">
                <i class="ph ph-x"></i>
              </button>
            </div>
          </div>

          <!-- Board Sections -->
          <div class="boards-container">
            <TransitionGroup name="board-list">
              <template v-for="board in boards" :key="board.idx">
                <section 
                  class="board-section" 
                  v-if="selBoard === 'all' || selBoard === board.idx"
                >
                  <div class="section-header">
                    <div class="section-title">
                      <div class="title-dot" :style="{ background: getBoardColor(board.idx) }"></div>
                      <h3>{{ board.name }}</h3>
                      <span class="section-count">{{ itemsByBoard[board.idx]?.length || 0 }}</span>
                    </div>
                    <button v-if="canWrite" class="write-btn" @click="createPost()">
                      <i class="ph ph-plus"></i>
                      <span>글 쓰기</span>
                    </button>
                  </div>

                  <!-- Empty State -->
                  <div v-if="filteredItems(board.idx).length === 0" class="empty-state">
                    <div class="empty-illustration">
                      <i class="ph-duotone ph-folder-dashed"></i>
                    </div>
                    <h4>아직 게시물이 없어요</h4>
                    <p>첫 번째 글을 작성해보세요!</p>
                    <button class="empty-write-btn" @click="createPost()">
                      <i class="ph ph-pencil-simple"></i>
                      글 쓰기
                    </button>
                  </div>
                  
                  <!-- Items Grid -->
                  <ul v-else class="items-grid">
                    <TransitionGroup name="item-list">
                      <li 
                        v-for="(item, index) in filteredItems(board.idx)" 
                        :key="item.idx" 
                        class="item-card"
                        :style="{ '--delay': index * 0.05 + 's' }"
                        @click="viewPost(item)"
                      >
                        <div class="thumb-wrapper">
                          <img 
                            :src="item.thumb && item.thumb.trim() !== '' ? item.thumb : '/imgs/empty_image.png'" 
                            alt="Thumbnail" 
                            class="thumb"
                            loading="lazy"
                          />
                          <div class="thumb-overlay">
                            <i class="ph ph-eye"></i>
                          </div>
                        </div>
                        <div class="item-content">
                          <h4 class="item-title">{{ item.title }}</h4>
                          <div class="item-meta">
                            <div class="author">
                              <div class="author-avatar" :style="{ background: getAvatarColor(item.mb_id) }">
                                {{ item.user_nick?.charAt(0) || '?' }}
                              </div>
                              <span class="author-name">{{ item.user_nick }}</span>
                            </div>
                            <span class="item-date">{{ formatKoreanTime(item.created_at) }}</span>
                          </div>
                        </div>
                      </li>
                    </TransitionGroup>
                  </ul>
                </section>
              </template>
            </TransitionGroup>
          </div>

        </main>

      </div>

      <!-- Mobile Bottom Navigation -->
      <nav class="mobile-nav">
        <button 
          :class="['nav-item', { active: selBoard === 'all' }]"
          @click="selBoard = 'all'"
        >
          <i class="ph-duotone ph-squares-four"></i>
          <span>전체</span>
        </button>
        <button 
          v-for="board in boards.slice(0, 3)" 
          :key="board.idx"
          :class="['nav-item', { active: selBoard === board.idx }]"
          @click="selBoard = board.idx"
        >
          <i class="ph-duotone ph-folder"></i>
          <span>{{ board.name }}</span>
        </button>
        <button v-if="boards.length > 3" class="nav-item" @click="showMoreCategories = true">
          <i class="ph-duotone ph-dots-three-circle"></i>
          <span>더보기</span>
        </button>
      </nav>

      <!-- Floating Action Button (Mobile) -->
      <button v-if="canWrite" class="fab" @click="createPost()" aria-label="글 쓰기">
        <i class="ph-bold ph-plus"></i>
      </button>

    </div>

  </div>
</template>

<script>
import { inject, ref, onMounted, computed } from "vue";
import dialog_boardView from "@/components/dialogs/boardView.vue";
import dialog_boardEdit from "@/components/dialogs/boardEdit.vue";
import { useBackButton } from '@/utils/useBackBtn';

export default {
  name: "viewReferenceOther",
  data() {
    return {
      pageId: "Reference_other",
    };
  },
  props: {
    oth_showBoardMain: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    dialog_boardView,
    dialog_boardEdit
  },
  setup(props) {
  
    // Inject
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const getUserProfileInfo = inject('getUserProfileInfo');
    const windowState = inject('windowState');
    const openWindow = inject('openWindow');
    const closeWindow = inject('closeWindow');

    // Basic state
    const boards = ref([]);
    const itemsByBoard = ref({});
    const watchingPost = ref(null);
    const boardViewShowing = ref(false);
    const selBoard = ref('all');
    const searchQuery = ref('');
    const searchFocused = ref(false);
    const showMoreCategories = ref(false);

    // Computed
    const totalCount = computed(() => {
      return Object.values(itemsByBoard.value).reduce((sum, items) => sum + (items?.length || 0), 0);
    });

    useBackButton(() => {
      props.oth_showBoardMain(false);
    });

    // Board colors for visual distinction
    const boardColors = [
      'linear-gradient(135deg, #FF6B6B, #FF8E53)',
      'linear-gradient(135deg, #4ECDC4, #44A08D)',
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #f093fb, #f5576c)',
      'linear-gradient(135deg, #4facfe, #00f2fe)',
      'linear-gradient(135deg, #43e97b, #38f9d7)',
    ];

    function getBoardColor(idx) {
      return boardColors[idx % boardColors.length];
    }

    function getAvatarColor(mbId) {
      const colors = [
        '#FF6B6B', '#4ECDC4', '#667eea', '#f093fb', '#4facfe', '#43e97b',
        '#ffa502', '#ff6348', '#7bed9f', '#70a1ff', '#eccc68', '#a29bfe'
      ];
      const hash = String(mbId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    }

    function filteredItems(boardIdx) {
      const items = itemsByBoard.value[boardIdx] || [];
      if (!searchQuery.value.trim()) return items;
      
      const query = searchQuery.value.toLowerCase();
      return items.filter(item => 
        item.title?.toLowerCase().includes(query) ||
        item.user_nick?.toLowerCase().includes(query)
      );
    }

    async function fetchBoards() {
      boards.value = [];
      let sql = `SELECT * FROM board_category where status = '1' and view_only = '1' ORDER BY sort_order ASC`;
      if (member_info.level <= 1) {
        sql = `SELECT * FROM board_category where status = '1' ORDER BY sort_order ASC`;
      }
      const response = await sql_query(sql);
      boards.value = response;

      for (const board of response) {
        await fetchItems(board.idx);
      }
    }

    async function fetchItems(board_idx) {
      try {
        const sql = `SELECT * FROM board_reference WHERE category_id = ${board_idx} and is_temp = '0' ORDER BY updated_at DESC LIMIT 0, 30`;
        const response = await sql_query(sql);

        if (response.length > 0) {
          const enrichedItems = await Promise.all(response.map(async (item) => {
            const userInfo = await getUserProfileInfo(item.mb_id);
            return {
              ...item,
              user_nick: userInfo?.nickname || '알 수 없음',
              user_pic: userInfo?.image || null,
            };
          }));

          itemsByBoard.value[board_idx] = enrichedItems;
        } else {
          itemsByBoard.value[board_idx] = [];
        }
      } catch (error) {
        console.error("❌ Error fetching board items:", error);
        itemsByBoard.value[board_idx] = [];
      }
    }

    function formatKoreanTime(dateString) {
      const now = new Date();
      const target = new Date(dateString);

      const koreaOffset = 9 * 60 * 60 * 1000;
      const localNow = new Date(now.getTime() + koreaOffset);
      const localTarget = new Date(target.getTime() + koreaOffset);

      const diffMs = localNow - localTarget;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHr / 24);

      const isSameDay = localNow.getFullYear() === localTarget.getFullYear()
        && localNow.getMonth() === localTarget.getMonth()
        && localNow.getDate() === localTarget.getDate();

      if (diffSec < 60) return '방금 전';
      if (diffMin < 60) return `${diffMin}분 전`;
      if (isSameDay) return `${diffHr}시간 전`;
      if (diffDay === 1) return '어제';
      if (diffDay < 7) return `${diffDay}일 전`;

      return `${localTarget.getMonth() + 1}월 ${localTarget.getDate()}일`;
    }

    function viewPost(item) {
      watchingPost.value = item;
      boardViewOnOff(true);
    }

    function boardViewOnOff(b) {
      if (b) {
        boardViewShowing.value = true;
      } else {
        boardViewShowing.value = b;
        watchingPost.value = null;
      }
    }

    function boardEditOnOff(b) {
      if (b) {
        postEditorVisible.value = true;
      } else {
        postEditorVisible.value = false;
        watchingPost.value = null;
      }
    }
    
    const postEditorVisible = ref(false);
    function createPost() {
      watchingPost.value = null;
      postEditorVisible.value = true;
    }

    onMounted(() => {
      fetchBoards();
    });

    const canWrite = computed(() => {
      return member_info.level <= 1; // 관리자만 글쓰기 가능
    });

    return {
      windowState,
      openWindow,
      closeWindow,
      boards,
      itemsByBoard,
      formatKoreanTime,
      viewPost,
      watchingPost,
      boardViewOnOff,
      fetchBoards,
      createPost,
      postEditorVisible,
      boardEditOnOff,
      selBoard,
      searchQuery,
      searchFocused,
      showMoreCategories,
      totalCount,
      getBoardColor,
      getAvatarColor,
      filteredItems,
      canWrite
    };
  },
};
</script>

<style scoped>
/* ========================================
   Base Styles & Variables
======================================== */
#dialog-board-main {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background: var(--color-background);
  z-index: 999;
  overflow: hidden;
}

.reference {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ========================================
   Header Styles
======================================== */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 0 16px;
  box-sizing: border-box;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-surface-reverse);
  flex-shrink: 0;
  position: relative;
  z-index: 10;
}

.back-btn,
.menu-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--color-surface-reverse);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover,
.menu-btn:hover {
  background: var(--color-brand);
  color: var(--color-text-black);
  transform: scale(1.05);
}

.back-btn:active,
.menu-btn:active {
  transform: scale(0.95);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 28px;
  color: var(--color-brand);
}

.header-title h1 {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

/* ========================================
   Content Layout
======================================== */
.content {
  flex: 1;
  display: flex;
  overflow: hidden;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  gap: 24px;
  box-sizing: border-box;
}

/* ========================================
   Sidebar Styles
======================================== */
.sidebar {
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sidebar-header {
  padding: 0 12px 12px;
}

.sidebar-label {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.menu {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
}

.menu-item:hover {
  background: var(--color-surface-reverse);
}

.menu-item.active {
  background: var(--color-brand);
  color: var(--color-text-black);
}

.menu-item.active .menu-count {
  background: rgba(0, 0, 0, 0.15);
  color: var(--color-text-black);
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.menu-item-content i {
  font-size: 20px;
}

.menu-item-content span {
  font-size: var(--font-size-base);
  font-weight: 500;
}

.menu-count {
  font-size: var(--font-size-sm);
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: var(--color-surface-reverse);
  min-width: 28px;
  text-align: center;
}

/* ========================================
   Main List Area
======================================== */
.list-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* ========================================
   Content Header & Search
======================================== */
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 16px;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.current-category {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.item-count {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--color-surface-reverse);
  border-radius: 12px;
  padding: 0 16px;
  height: 44px;
  min-width: 240px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.search-box.focused {
  border-color: var(--color-brand);
  background: var(--color-background);
  box-shadow: 0 0 0 4px rgba(255, 165, 0, 0.15);
}

.search-icon {
  font-size: 18px;
  color: var(--color-text-secondary);
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  outline: none;
}

.search-box input::placeholder {
  color: var(--color-text-secondary);
}

.clear-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-surface);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: var(--color-special-warning);
  color: white;
}

/* ========================================
   Boards Container
======================================== */
.boards-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.boards-container::-webkit-scrollbar {
  width: 6px;
}

.boards-container::-webkit-scrollbar-track {
  background: transparent;
}

.boards-container::-webkit-scrollbar-thumb {
  background: var(--color-surface-reverse);
  border-radius: 3px;
}

.boards-container::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-secondary);
}

/* ========================================
   Board Section
======================================== */
.board-section {
  margin-bottom: 48px;
  animation: fadeInUp 0.4s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.section-title h3 {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
}

.section-count {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  background: var(--color-surface-reverse);
  padding: 4px 10px;
  border-radius: 20px;
}

.write-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.write-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 165, 0, 0.4);
}

.write-btn:active {
  transform: translateY(0);
}

/* ========================================
   Empty State
======================================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: var(--color-surface-reverse);
  border-radius: 20px;
  text-align: center;
}

.empty-illustration {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--color-surface-reverse);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-illustration i {
  font-size: 40px;
  color: var(--color-text-secondary);
}

.empty-state h4 {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0 0 24px 0;
}

.empty-write-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.empty-write-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(255, 165, 0, 0.4);
}

/* ========================================
   Items Grid
======================================== */
.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.item-card {
  background: var(--color-surface-reverse);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: fadeInCard 0.4s ease forwards;
  animation-delay: var(--delay);
  opacity: 0;
}

@keyframes fadeInCard {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.item-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.item-card:hover .thumb-overlay {
  opacity: 1;
}

.item-card:hover .thumb {
  transform: scale(1.05);
}

.thumb-wrapper {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: var(--color-background-light);
}

.thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumb-overlay i {
  font-size: 32px;
  color: white;
}

.item-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.item-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.author {
  display: flex;
  align-items: center;
  gap: 8px;
}

.author-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: white;
}

.author-name {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: 500;
}

.item-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* ========================================
   Mobile Bottom Navigation
======================================== */
.mobile-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: var(--color-background);
  border-top: 1px solid var(--color-surface-reverse);
  padding: 0 8px;
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 100;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px 4px;
}

.nav-item.active {
  color: var(--color-brand);
}

.nav-item i {
  font-size: 22px;
}

.nav-item span {
  font-size: var(--font-size-xs);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60px;
}

/* ========================================
   Floating Action Button (Mobile)
======================================== */
.fab {
  display: none;
  position: fixed;
  bottom: 84px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(255, 165, 0, 0.4);
  transition: all 0.3s ease;
  z-index: 99;
}

.fab:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 28px rgba(255, 165, 0, 0.5);
}

.fab:active {
  transform: scale(0.95);
}

/* ========================================
   Transitions
======================================== */
.board-list-enter-active,
.board-list-leave-active {
  transition: all 0.4s ease;
}

.board-list-enter-from,
.board-list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.item-list-enter-active,
.item-list-leave-active {
  transition: all 0.3s ease;
}

.item-list-enter-from,
.item-list-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* ========================================
   Responsive: Tablet
======================================== */
@media (max-width: 1024px) {
  .content {
    padding: 16px;
    gap: 16px;
  }

  .sidebar {
    width: 180px;
  }

  .items-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
}

/* ========================================
   Responsive: Mobile
======================================== */
@media (max-width: 768px) {
  .content {
    padding: 16px;
    padding-bottom: 140px;
  }

  .sidebar {
    display: none;
  }

  .mobile-nav {
    display: flex;
  }

  .fab {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .write-btn {
    display: none;
  }

  .content-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: unset;
    width: 100%;
  }

  .items-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .item-content {
    padding: 12px;
  }

  .item-title {
    font-size: var(--font-size-sm);
  }

  .author-avatar {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }

  .empty-state {
    padding: 40px 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

/* ========================================
   Responsive: Small Mobile
======================================== */
@media (max-width: 480px) {
  .header {
    padding: 0 12px;
    height: 56px;
  }

  .back-btn,
  .menu-btn {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .header-title h1 {
    font-size: var(--font-size-lg);
  }

  .title-icon {
    font-size: 24px;
  }

  .items-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .thumb-wrapper {
    aspect-ratio: 4/3;
  }

  .item-content {
    padding: 10px;
    gap: 8px;
  }

  .item-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .author {
    width: 100%;
  }

  .item-date {
    width: 100%;
    text-align: right;
  }
}
</style>